import { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useExperiences } from 'core/contexts/experienceV2';
import { useLayout } from 'core/contexts/layout';
import { ExperienceStatus } from '@utriper/core';
import Layout from 'components/Layout';
import { Section } from 'modules/Pricing/styles';
import { Chip, CircularIndeterminate, DropdownButton } from '@utriper/core-ui';
import { Grid2, Typography, useMediaQuery } from '@mui/material';
import Placeholder from 'assets/images/placeholder.webp';
import SectionButtons from './components/SectionButtons';
import UpdateStatusForm from '../components/UpdateStatusForm';
import { getStatusColor } from 'components/ExperienceStatusChip';

const ExperienceIdPage = () => {
    const { id } = useParams();
    const experienceId = parseInt(id);
    const { i18n, t } = useTranslation();
    const translate = (key) => t(`experience_id.${key}`);
    const history = useHistory();
    const { getExperienceById, loading, experience } = useExperiences();
    const { open } = useLayout();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const [openStatusForm, setOpenStatusForm] = useState(false);
    const [statusToUpdate, setStatusToUpdate] = useState(null);

    const statusColor = getStatusColor(experience?.status ?? 0);
    const statusLabel = ExperienceStatus.getTranslatedName(ExperienceStatus.status, experience?.status ?? 0, i18n.language);

    const imageSrc = useMemo(() => {
        if (experience?.pictureURL) return `${process.env.REACT_APP_CDN_URL}${experience?.pictureURL}`;
        return Placeholder;
    }, [experience]);
    const headerStyles = useMemo(() => {
        if (!open) return;

        return {
            paddingLeft: { xs: '100vw', md: '240px' },
            transition: (theme) =>
                theme.transitions.create('padding', {
                    easing: theme.transitions.easing.easeOut,
                    duration: theme.transitions.duration.enteringScreen,
                }),
        };
    }, [open]);

    const actionButtons = useMemo(() => {
        let status = ExperienceStatus.IDS.live;

        if (experience?.status === status) {
            status = ExperienceStatus.IDS.archived;
        }

        const actions = [
            {
                id: 'edit',
                label: translate('actions.edit'),
                onClick: () => history.push(`/experience/${experienceId}/edit`),
            },
            // {
            //     id: 'duplicate',
            //     label: translate('actions.duplicate'),
            //     onClick: () => {},
            // },
            {
                id: status,
                label: experience?.status === ExperienceStatus.IDS.live ? translate('actions.archive') : translate('actions.publish'),
                onClick: () => {
                    setOpenStatusForm(true);
                    setStatusToUpdate(status);
                },
            },
        ];
        return actions;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experienceId, experience]);

    useEffect(() => {
        getExperienceById(experienceId, history);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experienceId]);

    return (
        <Layout>
            {loading ? (
                <CircularIndeterminate centered />
            ) : (
                <Section style={{ overflow: 'visible' }}>
                    {/* Header */}
                    <Grid2
                        container
                        sx={{
                            // TODO: MOVE STYLES TO ./styles.js
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: 'fit-content',
                            paddingLeft: { xs: 0, md: open ? '240px' : '64px' },

                            backgroundImage: `url(${imageSrc})`,
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',

                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                backgroundColor: 'rgba(0,0,0,0.35)',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                                zIndex: 1,
                                pointerEvents: 'none',
                            },
                            ...headerStyles,
                        }}
                    >
                        <Grid2 container size={12} padding="32px" justifyContent="space-between" alignItems="center" zIndex={2}>
                            {/* Title */}
                            <Grid2
                                container
                                size={10}
                                direction="column"
                                justifyContent="flex-start"
                                alignItems="flex-start"
                                gap="8px"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <Grid2 container size={12} spacing={1} alignItems="center" flexWrap="nowrap">
                                    <Typography fontSize={20} fontWeight={900} sx={{ color: '#FFFDF9' }} noWrap>
                                        {experience?.name}
                                    </Typography>
                                    <Chip label={statusLabel} sx={{ background: `${statusColor}`, color: '#fff', padding: '8px 4px', borderRadius: '4px' }} />
                                </Grid2>
                                <Typography fontSize={16} fontWeight={500} sx={{ color: '#FFFDF9', maxWidth: '100%' }} noWrap>
                                    {experience?.description1}
                                </Typography>
                            </Grid2>

                            {/* Dropdown Button */}
                            <Grid2 container size={2} justifyContent="flex-end">
                                <DropdownButton
                                    actions={actionButtons}
                                    variant="contained"
                                    buttonSx={{
                                        textTransform: 'none',
                                        backgroundColor: '#131313',
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#333',
                                        },
                                    }}
                                    dropdownSx={{
                                        backgroundColor: '#131313',
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#333',
                                        },
                                    }}
                                    menuItemSx={{
                                        '&:hover': {
                                            backgroundColor: '#f0f0f0',
                                        },
                                    }}
                                    iconButton={isMobile}
                                    iconButtonSx={{
                                        backgroundColor: '#131313',
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#333',
                                        },
                                        borderRadius: '4px',
                                    }}
                                />
                            </Grid2>
                        </Grid2>
                    </Grid2>

                    {/* Section Buttons */}
                    <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="120px">
                        <Typography fontSize={20} fontWeight={700} sx={{ color: '#131313' }} noWrap>
                            {translate('sections.title')}
                        </Typography>
                        <Typography fontSize={16} fontWeight={500} sx={{ color: '#131313bf' }}>
                            {translate('sections.subtitle')}
                        </Typography>
                    </Grid2>
                    <SectionButtons experienceId={experienceId} />
                </Section>
            )}

            {openStatusForm && (
                <UpdateStatusForm
                    open={openStatusForm}
                    handleClose={() => {
                        setOpenStatusForm(false);
                        setStatusToUpdate(null);
                    }}
                    status={statusToUpdate}
                    experienceId={experienceId}
                    experienceIds={[]}
                />
            )}
        </Layout>
    );
};

export default ExperienceIdPage;
